import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    /* ── Host ───────────────────────────────────────────────────────── */
    :host {
      display: block;
      border:        var(--uwc-panel-border,  1px solid ${t.border});
      border-radius: var(--uwc-panel-radius,  ${t.radiusLg});
      background:    var(--uwc-panel-bg,      ${t.surface});
      box-shadow:    var(--uwc-panel-shadow,  ${t.shadowSm});
      overflow:      hidden;
    }

    /* ── Header ─────────────────────────────────────────────────────── */
    .uwc-panel__header {
      display:         flex;
      align-items:     center;
      gap:             ${t.space2};
      padding:         var(--uwc-panel-header-padding, ${t.space3} ${t.space5});
      background:      var(--uwc-panel-header-bg,      transparent);
      border-bottom:   var(--uwc-panel-header-border,  1px solid ${t.border});
      color:           var(--uwc-panel-header-color,   ${t.text});
      font-size:       var(--uwc-panel-header-font-size, ${t.fontSizeLg});
      font-weight:     var(--uwc-panel-header-font-weight, ${t.fontWeightSemibold});
      line-height:     1.4;
      min-height:      3rem;
    }

    /* When collapsed, header has no bottom border (content is hidden) */
    :host([collapsed]) .uwc-panel__header {
      border-bottom-color: transparent;
    }

    /* ── Header icon ─────────────────────────────────────────────────── */
    .uwc-panel__header-icon {
      display:         inline-flex;
      align-items:     center;
      flex-shrink:     0;
      color:           var(--uwc-panel-icon-color, ${t.primary});
    }

    /* ── Header title ────────────────────────────────────────────────── */
    .uwc-panel__title {
      flex:          1;
      min-width:     0;
      overflow:      hidden;
      text-overflow: ellipsis;
      white-space:   nowrap;
    }

    /* ── Header icons slot (custom actions) ──────────────────────────── */
    .uwc-panel__header-actions {
      display:     inline-flex;
      align-items: center;
      gap:         ${t.space1};
      margin-left: auto;
      flex-shrink: 0;
    }

    /* ── Toggle button ───────────────────────────────────────────────── */
    .uwc-panel__toggler {
      display:          inline-flex;
      align-items:      center;
      justify-content:  center;
      border:           none;
      background:       transparent;
      color:            var(--uwc-panel-toggler-color, ${t.textSecondary});
      cursor:           pointer;
      padding:          0;
      flex-shrink:      0;
      /* A <button> resets font-size to the UA default, which would freeze the
         chevron icon (sized in em). Inherit the header's font-size so the
         chevron rescales with the size/font presets. */
      font-size:        inherit;
      transition:       color ${t.durationBase};
    }

    .uwc-panel__toggler:hover {
      color: var(--uwc-color-primary, ${t.primary});
    }

    .uwc-panel__toggler:focus-visible {
      outline:        2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 2px;
      border-radius:  ${t.radiusSm};
    }

    /* Chevron rotation — open state */
    .uwc-panel__toggler-icon {
      display:    inline-flex;
      transform:  rotate(0deg);
      transition: transform 250ms ease;
    }

    :host(:not([collapsed])) .uwc-panel__toggler-icon {
      transform: rotate(180deg);
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-panel__toggler-icon { transition: none; }
      .uwc-panel__toggler      { transition: none; }
    }

    /* ── Content wrapper (WAAPI animated) ───────────────────────────── */
    .uwc-panel__content-wrap {
      /* Height animated by JS; overflow applied inline during animation */
    }

    /* ── Content body ────────────────────────────────────────────────── */
    .uwc-panel__content {
      padding:    var(--uwc-panel-content-padding, ${t.space4} ${t.space5});
      color:      var(--uwc-panel-content-color,   ${t.text});
      font-size:  var(--uwc-panel-content-font-size, ${t.fontSizeMd});
      line-height: 1.6;
    }

    /* ── Footer ─────────────────────────────────────────────────────── */
    .uwc-panel__footer {
      padding:      var(--uwc-panel-footer-padding, ${t.space3} ${t.space5});
      background:   var(--uwc-panel-footer-bg,      transparent);
      border-top:   var(--uwc-panel-footer-border,  1px solid ${t.border});
      font-size:    ${t.fontSizeMd};
      color:        ${t.textSecondary};
    }

    /* Footer hidden when slot is empty */
    .uwc-panel__footer--hidden {
      display: none;
    }

    /* ── No-header mode ──────────────────────────────────────────────── */
    :host([no-header]) .uwc-panel__header {
      display: none;
    }
  `,
];
